import { beautifyNumber } from '../FormatNumber';

const SECTION_START = '<!--partners-equity-section:start-->';
const SECTION_END = '<!--partners-equity-section:end-->';
export const PARTNERS_EQUITY_SECTION_CLASS = 'partners-equity-section';

const roundCurrency = (value) => Math.round((Number(value) || 0) * 100) / 100;

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatAmount = (value) => beautifyNumber(roundCurrency(value)).trim();

const formatEquity = (value) => `${(Number(value) || 0).toFixed(2)}%`;

export const isPartnershipClient = (client) =>
  Boolean(client?.isPartners) || client?.caseType === 'שותפות';

export const hasPartnersForDistribution = (client, partners) =>
  isPartnershipClient(client) && Array.isArray(partners) && partners.length > 0;

/** Each partner's share of `total` according to their equity percentage. */
export const buildPartnersDistribution = (partners = [], total = 0) => {
  const base = Number(total) || 0;
  const rows = (Array.isArray(partners) ? partners : []).map((partner) => {
    const equity = Number(partner?.equity) || 0;
    return {
      id: partner?.id,
      name: partner?.name || '',
      partnerId: partner?.partnerId || '',
      equity,
      amount: roundCurrency((base * equity) / 100),
    };
  });

  return {
    rows,
    total: base,
    totalEquity: rows.reduce((sum, row) => sum + row.equity, 0),
    totalAmount: roundCurrency(rows.reduce((sum, row) => sum + row.amount, 0)),
  };
};

const amountCellAttrs = (amount) => `class="partners-equity-amount${amount < 0 ? ' is-negative' : ''}"`;

const SECTION_STYLES = `
  .${PARTNERS_EQUITY_SECTION_CLASS} {
    margin: 24px 0 8px;
    direction: rtl;
    page-break-inside: avoid;
    break-inside: avoid;
    font-family: Arial, Helvetica, sans-serif;
    color: #000;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .${PARTNERS_EQUITY_SECTION_CLASS} .partners-equity-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 8px;
    margin: 0 0 6px;
    padding: 0 0 6px;
    border-bottom: 2px solid #000;
  }
  .${PARTNERS_EQUITY_SECTION_CLASS} .partners-equity-title {
    font-size: 16px;
    font-weight: 700;
    color: #000;
  }
  .${PARTNERS_EQUITY_SECTION_CLASS} .partners-equity-base {
    font-size: 13px;
    font-weight: 600;
    color: #333;
  }
  .${PARTNERS_EQUITY_SECTION_CLASS} .partners-equity-table {
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    table-layout: fixed;
    font-size: 12px;
  }
  .${PARTNERS_EQUITY_SECTION_CLASS} .partners-equity-table th,
  .${PARTNERS_EQUITY_SECTION_CLASS} .partners-equity-table td {
    border: none;
    padding: 4px 8px;
    text-align: right;
    vertical-align: middle;
    word-wrap: break-word;
    background: #ffffff;
    color: #000;
  }
  .${PARTNERS_EQUITY_SECTION_CLASS} .partners-equity-table thead th {
    border-bottom: 1px solid #000;
    font-size: 13px;
    font-weight: 700;
  }
  .${PARTNERS_EQUITY_SECTION_CLASS} .partners-equity-total-row td {
    font-weight: 700;
    background-color: #e8e8e8;
    border-top: 2px solid #000;
    border-bottom: 3px double #000;
    padding-top: 8px;
    padding-bottom: 8px;
  }
  .${PARTNERS_EQUITY_SECTION_CLASS} .partners-equity-amount {
    direction: ltr;
    unicode-bidi: plaintext;
  }
`;

/**
 * HTML block (Hebrew, like the rest of the HTML reports) listing every partner's share of `total`.
 * Returns '' when there are no partners so callers can use it to remove a stale section.
 */
export const buildPartnersEquitySectionHtml = (partners = [], total = 0) => {
  const { rows, total: base, totalEquity, totalAmount } = buildPartnersDistribution(partners, total);
  if (!rows.length) return '';

  const bodyRows = rows
    .map(
      (row) => `
        <tr>
          <td>${escapeHtml(row.name)}</td>
          <td>${escapeHtml(row.partnerId)}</td>
          <td>${formatEquity(row.equity)}</td>
          <td ${amountCellAttrs(row.amount)} data-partner-equity="${row.equity}">${formatAmount(row.amount)}</td>
        </tr>`,
    )
    .join('');

  return `${SECTION_START}
    <div class="${PARTNERS_EQUITY_SECTION_CLASS}" dir="rtl" data-partners-total="${base}">
      <style>${SECTION_STYLES}</style>
      <div class="partners-equity-header">
        <div class="partners-equity-title">חלוקה לשותפים</div>
        <div class="partners-equity-base">סה״כ לחלוקה: <span ${amountCellAttrs(base)} data-partners-base-amount="true">${formatAmount(base)}</span></div>
      </div>
      <table class="partners-equity-table">
        <thead>
          <tr>
            <th>שם השותף</th>
            <th>ת״ז שותף</th>
            <th>אחוז בעלות (%)</th>
            <th>סה״כ</th>
          </tr>
        </thead>
        <tbody>
          ${bodyRows}
          <tr class="partners-equity-total-row">
            <td>סה״כ</td>
            <td></td>
            <td>${formatEquity(totalEquity)}</td>
            <td ${amountCellAttrs(totalAmount)} data-partners-total-amount="true">${formatAmount(totalAmount)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  ${SECTION_END}`;
};

/**
 * Replace an existing partners section, or insert `sectionHtml` inside the report's outer
 * wrapper (the last `</div>` right before `</body>`). Passing '' removes the section.
 */
export const upsertPartnersEquitySection = (html, sectionHtml) => {
  if (!html) return html;

  const startIdx = html.indexOf(SECTION_START);
  if (startIdx !== -1) {
    const endIdx = html.indexOf(SECTION_END, startIdx);
    if (endIdx !== -1) {
      return html.slice(0, startIdx) + (sectionHtml || '') + html.slice(endIdx + SECTION_END.length);
    }
  }

  if (!sectionHtml) return html;

  const lower = html.toLowerCase();
  const bodyCloseIdx = lower.lastIndexOf('</body>');
  if (bodyCloseIdx === -1) return html + sectionHtml;

  const lastDivIdx = lower.lastIndexOf('</div>', bodyCloseIdx);
  const insideWrapper =
    lastDivIdx !== -1 && html.slice(lastDivIdx + '</div>'.length, bodyCloseIdx).trim() === '';
  const insertAt = insideWrapper ? lastDivIdx : bodyCloseIdx;

  return html.slice(0, insertAt) + sectionHtml + html.slice(insertAt);
};

export const applyPartnersEquitySection = (html, partners = [], total = 0) =>
  upsertPartnersEquitySection(html, buildPartnersEquitySectionHtml(partners, total));

const setAmountElement = (el, amount) => {
  el.textContent = formatAmount(amount);
  el.classList.toggle('is-negative', amount < 0);
};

/** Recompute the amounts of an already-rendered partners section (used after in-place edits). */
export const refreshPartnersEquitySectionInDocument = (doc, total) => {
  const section = doc?.querySelector?.(`.${PARTNERS_EQUITY_SECTION_CLASS}`);
  if (!section) return;

  const base = roundCurrency(total);
  section.setAttribute('data-partners-total', String(base));
  let sum = 0;
  section.querySelectorAll('[data-partner-equity]').forEach((cell) => {
    const equity = Number(cell.getAttribute('data-partner-equity')) || 0;
    const amount = roundCurrency((base * equity) / 100);
    sum += amount;
    setAmountElement(cell, amount);
  });
  section.querySelectorAll('[data-partners-total-amount]').forEach((el) => setAmountElement(el, roundCurrency(sum)));
  section.querySelectorAll('[data-partners-base-amount]').forEach((el) => setAmountElement(el, base));
};

/** Partners + base total as currently rendered in a document (reflects in-place edits). */
export const readPartnersEquityFromDocument = (doc) => {
  const section = doc?.querySelector?.(`.${PARTNERS_EQUITY_SECTION_CLASS}`);
  if (!section) return null;

  const partners = [...section.querySelectorAll('[data-partner-equity]')].map((cell, index) => {
    const tds = cell.closest('tr')?.querySelectorAll('td') || [];
    return {
      id: index,
      name: tds[0]?.textContent?.trim() || '',
      partnerId: tds[1]?.textContent?.trim() || '',
      equity: Number(cell.getAttribute('data-partner-equity')) || 0,
    };
  });

  return { partners, total: Number(section.getAttribute('data-partners-total')) || 0 };
};
